// src/components/CoiDisclosureThresholdsPopup.jsx
// Sets the account's COI Disclosure Thresholds: the minimum amount, per
// company per program year, that a provider was expected to disclose, and
// how many program years back to check. Kept distinct from Payment Audit
// Thresholds on purpose: those decide when a provider's combined payments
// across all companies become a finding; these decide which payments from a
// single company a provider should have mentioned on their questionnaire.

import { useState, useEffect } from "react";
import { authedFetch } from "../utils/apiClient";

const FIELDS = [
  { key: "general", label: "General payments" },
  { key: "ownership", label: "Ownership value" },
  { key: "directResearch", label: "Direct research payments" },
  { key: "piResearch", label: "PI research payments" },
];

export default function CoiDisclosureThresholdsPopup({ userId, getToken, onClose }) {
  const [values, setValues] = useState({ general: "", ownership: "", directResearch: "", piResearch: "" });
  const [lookback, setLookback] = useState("1");
  const [allYears, setAllYears] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authedFetch(getToken, "/api/manage-coi-disclosure-thresholds", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, action: "get" }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load settings.");
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        const s = data.settings;
        setValues({
          general: s.generalPaymentsThreshold ?? "",
          ownership: s.ownershipThreshold ?? "",
          directResearch: s.directResearchThreshold ?? "",
          piResearch: s.piResearchThreshold ?? "",
        });
        if (s.lookbackYears === null) setAllYears(true);
        else setLookback(String(s.lookbackYears));
        if (data.configured) setMeta({ updatedByEmail: data.updatedByEmail, updatedAt: data.updatedAt });
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const nothingSet = FIELDS.every((f) => values[f.key] === "");

  async function handleSave() {
    if (saving) return;
    setError(null);

    const toNum = (v) => (v === "" ? null : Number(v));
    const parsed = FIELDS.map((f) => ({ ...f, value: toNum(values[f.key]) }));
    const bad = parsed.find((p) => p.value !== null && (!Number.isFinite(p.value) || p.value < 0));
    if (bad) { setError(`${bad.label} must be a number of 0 or more, or left blank.`); return; }

    let lookbackYears = null;
    if (!allYears) {
      const n = Number(lookback);
      if (!Number.isInteger(n) || n < 1) { setError("Program years to check must be a whole number of 1 or more."); return; }
      lookbackYears = n;
    }

    setSaving(true);
    try {
      const res = await authedFetch(getToken, "/api/manage-coi-disclosure-thresholds", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId, action: "set",
          generalPaymentsThreshold: parsed[0].value,
          ownershipThreshold: parsed[1].value,
          directResearchThreshold: parsed[2].value,
          piResearchThreshold: parsed[3].value,
          lookbackYears,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to save. Please try again."); return; }
      onClose();
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-navy-800 border border-white/10 rounded-xl p-5 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-white font-medium text-sm">COI Disclosure Thresholds</h3>
          <p className="text-white/40 text-xs mt-1">
            The minimum amount, from any single company in a single program year, that a provider is expected to
            disclose. Used to decide whether CMS Open Payments activity that a provider's questionnaire doesn't
            mention counts as a gap. Shared across your whole account; any team member can adjust it.
            Leave a field blank to skip that category; enter 0 to check any amount.
          </p>
          <p className="text-white/30 text-xs mt-2">
            Separate from Payment Audit Thresholds, which flag a provider's combined payments across all companies.
          </p>
        </div>

        {loading ? (
          <p className="text-white/30 text-xs">Loading current settings…</p>
        ) : (
          <>
            <div className="space-y-2">
              {FIELDS.map((f) => (
                <label key={f.key} className="flex items-center justify-between gap-2 text-xs text-white/60">
                  <span>{f.label}</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="not checked"
                    value={values[f.key]}
                    onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40"
                  />
                </label>
              ))}
            </div>

            <div className="space-y-1.5 pt-2 border-t border-white/5">
              <label className="flex items-center justify-between gap-2 text-xs text-white/60">
                <span>Program years to check</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={allYears ? "" : lookback}
                  placeholder={allYears ? "all" : ""}
                  disabled={allYears}
                  onChange={(e) => setLookback(e.target.value)}
                  className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40 disabled:opacity-40"
                />
              </label>
              <label className="flex items-center justify-end gap-2 text-xs text-white/50">
                <input type="checkbox" checked={allYears} onChange={(e) => setAllYears(e.target.checked)} />
                <span>All available years</span>
              </label>
              <p className="text-white/30 text-xs">
                1 checks only the most recent program year. The window moves forward automatically as new CMS data is loaded.
              </p>
            </div>

            {nothingSet && (
              <p className="text-amber-400/80 text-xs">
                No categories are set, so no gaps will be flagged until at least one is.
              </p>
            )}
            {meta && (
              <p className="text-white/25 text-xs">
                Last updated{meta.updatedByEmail ? ` by ${meta.updatedByEmail}` : ""}
                {meta.updatedAt ? ` on ${new Date(meta.updatedAt).toLocaleDateString()}` : ""}
              </p>
            )}
          </>
        )}

        {error && (
          <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">✗ {error}</p>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button onClick={handleSave} disabled={saving || loading} className="btn-primary text-sm px-5 py-2 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}